// Lightweight image download utilities for client-side WebP conversion
// Fetches an image URL as a Blob, converts to WebP via Canvas/OffscreenCanvas,
// then triggers a download with a sensible filename.

export type DownloadWebPOptions = {
  filename?: string
  quality?: number // 0..1
  signal?: AbortSignal
}

const supportsOffscreen = typeof OffscreenCanvas !== 'undefined'

async function blobToBitmap(blob: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if ('createImageBitmap' in window && typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(blob)
    } catch {
      // fall through to HTMLImageElement path
    }
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob)
    const img = new Image()
    // Use anonymous to avoid tainting canvas; fetch created a same-origin blob URL
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = (e) => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load image for conversion'))
    }
    img.src = url
  })
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export async function downloadUrlAsWebP(url: string, opts: DownloadWebPOptions = {}) {
  const { filename, quality = 0.92, signal } = opts
  // Fetch as CORS-enabled request so the resulting blob is same-origin
  const res = await fetch(url, { mode: 'cors', signal })
  if (!res.ok) throw new Error(`Failed to fetch image: ${res.status}`)
  const srcBlob = await res.blob()

  const bitmapOrImg = await blobToBitmap(srcBlob)
  const width = (bitmapOrImg as any).width
  const height = (bitmapOrImg as any).height

  let outBlob: Blob
  if (supportsOffscreen) {
    const canvas = new OffscreenCanvas(width, height)
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Unable to get 2D context')
    ctx.drawImage(bitmapOrImg as any, 0, 0, width, height)
    outBlob = await canvas.convertToBlob({ type: 'image/webp', quality })
  } else {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Unable to get 2D context')
    ctx.drawImage(bitmapOrImg as any, 0, 0, width, height)
    outBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((b) => {
        if (b) resolve(b)
        else reject(new Error('WebP conversion failed'))
      }, 'image/webp', quality)
    })
  }

  const outName = (filename && filename.trim() !== ''
    ? filename
    : (new URL(url).pathname.split('/').pop() || 'image')
  ).replace(/\.[^.]+$/, '') + '.webp'

  triggerDownload(outBlob, outName)
}

export async function downloadMultipleAsWebP(urls: string[], baseName = 'image', opts: Omit<DownloadWebPOptions, 'filename'> = {}) {
  let i = 1
  for (const u of urls) {
    const name = `${baseName}-${String(i).padStart(2, '0')}.webp`
    // Intentionally sequential to avoid overwhelming the browser/network with many fetches at once
     
    await downloadUrlAsWebP(u, { ...opts, filename: name })
    i += 1
  }
}

